'use client'

import React, { useEffect, useRef, useCallback } from 'react'
import Link from 'next/link'
import clsx from 'clsx'
import { useRouter, usePathname } from 'next/navigation'
import { useDispatch } from 'react-redux'
import type { AppDispatch } from '@/store'
import { changeNonMemberModalState } from '@/store/modal/slice'
import { LOCAL_STORAGE_KEYS } from '@/constants/storageKeys'
import styles from './styles.module.scss'
import Script from 'next/script'

interface LogoBtnProps {
  type: 'default' | 'noImage' | 'talentbook' | 'email' | 'twitter' | 'google' | 'google-custom'
  text: string
  link?: string
  textBold?: boolean
  external?: boolean
  className?: string
  round?: boolean
  used?: string
}

const IMAGE_SOURCES = {
  talentbook: '/images/svg/logo_icon.svg',
  email: '/images/svg/icon_email.svg',
  twitter: '/images/svg/icon_x.svg',
  google: '/images/svg/icon_google.svg',
} as const

const TWITTER_ENDPOINTS = {
  SIGNATURE: '/api/twitter/oauth/generate-signature',
  AUTHORIZE: 'https://api.twitter.com/oauth/authorize',
} as const

const LogoBtn: React.FC<LogoBtnProps> = ({
  type,
  text,
  link = '',
  textBold = false,
  external = false,
  className = '',
  round = false,
  used,
}) => {
  const router = useRouter()
  const pathname = usePathname()
  const dispatch = useDispatch<AppDispatch>()
  const googleScriptLoaded = useRef(false)
  const googleInitialized = useRef(false)
  const retryCount = useRef(0)

  const googleConfig = {
    clientId: process.env.NEXT_PUBLIC_MEMBER_GOOGLE_CLIENT_ID || '',
    signupRedirect: process.env.NEXT_PUBLIC_MEMBER_GOOGLE_SIGNUP_REDIRECT || '',
  }

  // Google認証ボタンの初期化処理
  const initializeAndRenderGoogleButton = useCallback(
    (googleButton: Element) => {
      if (typeof window === 'undefined' || !(window as any).google)
        return // Google認証成功時のcallbackでlocalStorageフラグを設定
      ;(window as any).google.accounts.id.initialize({
        client_id: googleConfig.clientId,
        callback: (response: any) => {
          // SNSログイン時のフラグを設定
          localStorage.setItem(LOCAL_STORAGE_KEYS.SNS_LOGIN, JSON.stringify(true))
          // Credential responseをlogin_uriに送信
          const form = document.createElement('form')
          form.method = 'POST'
          form.action = googleConfig.signupRedirect
          const input = document.createElement('input')
          input.type = 'hidden'
          input.name = 'credential'
          input.value = response.credential
          form.appendChild(input)
          document.body.appendChild(form)
          form.submit()
        },
      })

      try {
        ;(window as any).google.accounts.id.renderButton(googleButton, {
          type: 'standard',
          shape: 'rectangular',
          size: 'large',
          theme: 'outline',
          text: used === 'signup' ? 'signup_with' : 'signin_with',
          logo_alignment: 'center',
          width: '220',
        })
      } catch (error) {
        retryCount.current++
        if (retryCount.current > 1 && process.env.NODE_ENV === 'development') {
          console.error(
            `Google button re-initialization failed (attempt ${retryCount.current}):`,
            error,
          )
        }
      }
    },
    [googleConfig.clientId, googleConfig.signupRedirect, used],
  )

  useEffect(() => {
    if (type === 'google') {
      document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`)

      // Googleスクリプトの読み込み
      const initializeGoogleLogin = () => {
        const existingScript = document.querySelector(
          'script[src="https://accounts.google.com/gsi/client"]',
        )
        if (!existingScript) {
          const gsiClientScript = document.createElement('script')
          gsiClientScript.setAttribute('src', 'https://accounts.google.com/gsi/client')
          gsiClientScript.onload = () => {
            googleScriptLoaded.current = true
            // スクリプト読み込み後、少し待ってからボタンの初期化を確認
            setTimeout(() => {
              checkAndReinitializeGoogleButton()
            }, 500)
          }
          document.head.appendChild(gsiClientScript)
        } else {
          // スクリプトが既に存在する場合、ボタンの再初期化を確認
          setTimeout(() => {
            checkAndReinitializeGoogleButton()
          }, 200)
        }
      }

      // Googleボタンの存在確認と再初期化
      const checkAndReinitializeGoogleButton = () => {
        const googleIframe = document.querySelector('iframe[src*="accounts.google.com"]')
        const googleButton = document.querySelector('.g_id_signin')

        if (!googleIframe && googleButton) initializeAndRenderGoogleButton(googleButton)
      }

      if (!googleInitialized.current) {
        initializeGoogleLogin()
        googleInitialized.current = true
      } else {
        // SPA遷移の場合、Googleボタンの状態をチェックして再初期化
        setTimeout(() => {
          checkAndReinitializeGoogleButton()
        }, 100)
      }
    }
  }, [type, pathname, initializeAndRenderGoogleButton])

  // SPA遷移後のGoogleボタン状態を定期的にチェック
  useEffect(() => {
    if (type === 'google') {
      let checkInterval: ReturnType<typeof setInterval>

      const periodicCheck = () => {
        const googleIframe = document.querySelector('iframe[src*="accounts.google.com"]')
        const googleButton = document.querySelector('.g_id_signin')

        if (googleButton && !googleIframe) initializeAndRenderGoogleButton(googleButton)
      }

      // 2秒間隔でチェック（最大2回）
      const startTime = Date.now()
      checkInterval = setInterval(() => {
        if (Date.now() - startTime > 4000) {
          clearInterval(checkInterval)
          return
        }
        periodicCheck()
      }, 2000)

      return () => {
        if (checkInterval) {
          clearInterval(checkInterval)
        }
      }
    }
  }, [type, pathname, initializeAndRenderGoogleButton])

  const handleEmailClick = () => {
    router.push('/signup')
    dispatch(changeNonMemberModalState(false))
  }

  const handleTwitterLogin = async () => {
    // login後に元ページにリダイレクトする為のflag(リダイレクト自体はmiddleware,storage.setはNonMemberModal)
    // twitterはログインキャンセルが可能なため、twitterFlagをset
    localStorage.setItem(LOCAL_STORAGE_KEYS.SNS_LOGIN, JSON.stringify(true))

    try {
      // Use secure server-side endpoint for OAuth signature generation
      const response = await fetch(TWITTER_ENDPOINTS.SIGNATURE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          callback: process.env.NEXT_PUBLIC_MEMBER_TWITTER_OAUTH_CALLBACK || '',
        }),
        credentials: 'include',
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.text()
      location.href = `${TWITTER_ENDPOINTS.AUTHORIZE}?${data}`
    } catch (error) {
      console.error('Twitter login error:', error)
    }
  }

  // google-custom用: SNSログインフラグを設定（実際のクリックはオーバーレイされたGoogleボタンで処理）
  const handleGoogleCustomMouseEnter = useCallback(() => {
    localStorage.setItem(LOCAL_STORAGE_KEYS.SNS_LOGIN, JSON.stringify(true))
  }, [])

  const renderIcon = () => {
    if (type === 'noImage') return null

    if (type === 'default') {
      return (
        <span className={styles.logoWrapper}>
          <img src='/images/svg/logo_icon.svg' alt='logo' width={24} height={24} loading='lazy' />
        </span>
      )
    }

    // google-custom uses google icon
    const iconType = type === 'google-custom' ? 'google' : type
    const imageSrc = IMAGE_SOURCES[iconType as keyof typeof IMAGE_SOURCES]
    if (!imageSrc) return null

    const iconSize = type === 'twitter' ? { width: 20, height: 20 } : { width: 60, height: 60 }

    return (
      <div className={styles.iconWrapper}>
        <img
          src={imageSrc}
          alt={`${iconType} icon`}
          className={styles.iconImage}
          width={iconSize.width}
          height={iconSize.height}
          loading='lazy'
        />
      </div>
    )
  }

  const renderContent = () => (
    <>
      {renderIcon()}
      <span className={styles.buttonText}>{text}</span>
    </>
  )

  const additionalClass =
    className && className in styles ? styles[className as keyof typeof styles] : ''
  const usedClass = used && used in styles ? styles[used as keyof typeof styles] : ''
  const wrapperClasses = clsx(styles.logoButtonWrapper, additionalClass, usedClass)
  const getButtonClasses = () => {
    return clsx(styles.buttonBase, styles.otherLogin, {
      [styles.tbButton]: type === 'talentbook',
      [styles.noImage]: type === 'noImage',
      [styles.round]: round,
      [styles.groupButton]: used === 'group',
    })
  }

  // Special button types
  if (type === 'google') {
    return (
      <>
        <Script
          src='https://accounts.google.com/gsi/client'
          strategy='afterInteractive'
          onLoad={() => {
            googleScriptLoaded.current = true
            // スクリプト読み込み後に少し待ってからGoogleボタンを初期化
            setTimeout(() => {
              const googleIframe = document.querySelector('iframe[src*="accounts.google.com"]')
              const googleButton = document.querySelector('.g_id_signin')

              if (!googleIframe && googleButton) initializeAndRenderGoogleButton(googleButton)
            }, 100)
          }}
        />
        <div className={wrapperClasses}>
          <div className={styles.googleLogin}>
            <div
              className='g_id_signin'
              data-type='standard'
              data-shape='rectangular'
              data-size='large'
              data-theme='outline'
              data-text={used === 'signup' ? 'signup_with' : 'signin_with'}
              data-logo_alignment='center'
              data-width='220'
            />
          </div>
        </div>
      </>
    )
  }

  if (type === 'twitter') {
    return (
      <div className={wrapperClasses}>
        <div
          className={clsx(styles.xLogin, styles.buttonBase, {
            [styles.groupButton]: used === 'group',
          })}
          onClick={handleTwitterLogin}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleTwitterLogin()
            }
          }}
          role='button'
          tabIndex={0}
          aria-label='Login with X (Twitter)'
        >
          {renderContent()}
        </div>
      </div>
    )
  }

  if (type === 'google-custom') {
    return (
      <>
        <Script
          src='https://accounts.google.com/gsi/client'
          strategy='afterInteractive'
          onLoad={() => {
            googleScriptLoaded.current = true
            // オーバーレイボタンを初期化
            setTimeout(() => {
              const overlayButton = document.querySelector('.g_id_signin_overlay')
              if (overlayButton && (window as any).google) {
                ;(window as any).google.accounts.id.initialize({
                  client_id: googleConfig.clientId,
                  callback: (response: any) => {
                    localStorage.setItem(LOCAL_STORAGE_KEYS.SNS_LOGIN, JSON.stringify(true))
                    const form = document.createElement('form')
                    form.method = 'POST'
                    form.action = googleConfig.signupRedirect
                    const input = document.createElement('input')
                    input.type = 'hidden'
                    input.name = 'credential'
                    input.value = response.credential
                    form.appendChild(input)
                    document.body.appendChild(form)
                    form.submit()
                  },
                })
                const isSp = window.innerWidth <= 768
                const buttonWidth = used === 'group' ? (isSp ? '400' : '255') : '400'
                ;(window as any).google.accounts.id.renderButton(overlayButton, {
                  type: 'standard',
                  shape: 'rectangular',
                  size: 'large',
                  theme: 'outline',
                  text: 'signin_with',
                  logo_alignment: 'center',
                  width: buttonWidth,
                })
              }
            }, 100)
          }}
        />
        <div
          className={wrapperClasses}
          onMouseEnter={handleGoogleCustomMouseEnter}
          onTouchStart={handleGoogleCustomMouseEnter}
        >
          <div
            className={clsx(styles.googleCustomWrapper, { [styles.groupButton]: used === 'group' })}
          >
            {/* カスタムボタン（見た目用） */}
            <div
              className={clsx(styles.googleCustomLogin, styles.buttonBase, {
                [styles.groupButton]: used === 'group',
              })}
            >
              {renderContent()}
            </div>
            {/* 透明なGoogleボタン（実際のクリック処理用、上に重ねる） */}
            <div className={clsx('g_id_signin_overlay', styles.googleButtonOverlay)} />
          </div>
        </div>
      </>
    )
  }

  if (type === 'email') {
    return (
      <div className={wrapperClasses}>
        <div
          className={clsx(styles.buttonBase, { [styles.groupButton]: used === 'group' })}
          onClick={handleEmailClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleEmailClick()
            }
          }}
          role='button'
          tabIndex={0}
          aria-label='Sign up with email'
        >
          {renderContent()}
        </div>
      </div>
    )
  }

  // Regular link buttons
  const content = renderContent()

  if (external) {
    return (
      <div className={wrapperClasses}>
        <a href={link} className={getButtonClasses()} target='_blank' rel='noopener noreferrer'>
          {content}
        </a>
      </div>
    )
  }

  // Handle click for talentbook type to close modal
  const handleTalentbookClick = () => {
    if (type === 'talentbook') {
      dispatch(changeNonMemberModalState(false))
    }
  }

  return (
    <div className={wrapperClasses}>
      <Link href={link} className={getButtonClasses()} onClick={handleTalentbookClick}>
        {content}
      </Link>
    </div>
  )
}

export default LogoBtn
